import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ShoppingBag, Truck, Shield, HeadphonesIcon,
  Award, TrendingDown, Sparkles, Flame
} from 'lucide-react';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'outils-electriques': '🔧',
  'peinture': '🎨',
  'plomberie': '🚿',
  'electricite': '⚡',
  'quincaillerie': '🔩',
};

const Home = () => {
  const [bestsellers, setBestsellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [onSale, setOnSale] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products/bestsellers?limit=8`),
      axios.get(`${API}/products/new-arrivals?limit=4`),
      axios.get(`${API}/products/on-sale?limit=4`),
      axios.get(`${API}/categories`),
    ]).then(([b, n, s, c]) => {
      setBestsellers(b.data);
      setNewArrivals(n.data);
      setOnSale(s.data);
      setCategories(c.data);
    }).catch((e) => console.error(e));
  }, []);

  return (
    <div className="min-h-screen">
      {/* Top Reassurance Bar */}
      <div className="bg-brand-dark text-white text-xs md:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-center md:justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <Truck className="w-3.5 h-3.5 text-brand-orange" />
            <span>Livraison rapide 24-48h</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 text-brand-orange" />
            <span>Paiement 100% sécurisé</span>
          </div>
          <div className="flex items-center gap-1.5">
            <HeadphonesIcon className="w-3.5 h-3.5 text-brand-orange" />
            <span>Service client 6j/7</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Award className="w-3.5 h-3.5 text-brand-orange" />
            <span>Qualité professionnelle</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section
        className="hero-section relative h-[450px] md:h-[550px] flex items-center"
        style={{
          backgroundImage:
            'url(https://static.prod-images.emergentagent.com/jobs/83a3f96f-30f5-4fa6-a54a-334cb53e346a/images/9fccebc6014559e9c021405db14455ef1a0dfc38c43854ab7965ccfad75fd1e1.png)',
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-brand-orange text-white px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wide mb-4">
              Sélection Pro
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight"
              style={{ fontFamily: 'IBM Plex Sans' }}
            >
              Votre partenaire bricolage et chantier
            </h1>
            <p className="text-lg sm:text-xl mb-8 text-white/90">
              Outils, matériaux, fixation et équipements professionnels — livrés rapidement chez vous
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-3 rounded-sm font-semibold hover:bg-orange-600 transition-colors"
                data-testid="shop-now-button"
              >
                <ShoppingBag className="w-5 h-5" />
                Découvrir le catalogue
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'IBM Plex Sans' }}>
                Toutes nos catégories
              </h2>
              <p className="text-muted-foreground text-sm mt-1">Trouvez le produit qu'il vous faut</p>
            </div>
            <Link to="/products" className="text-brand-orange hover:underline text-sm font-medium hidden md:flex items-center gap-1">
              Voir tout <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${category.id}`}
                className="group bg-white border border-border hover:border-brand-orange rounded-sm p-4 md:p-6 text-center transition-all hover:-translate-y-1 hover:shadow-md"
                data-testid={`category-card-${category.id}`}
              >
                <div className="text-3xl md:text-4xl mb-3">
                  {CATEGORY_ICONS[category.id] || '📦'}
                </div>
                <h3 className="font-semibold mb-1 group-hover:text-brand-orange transition-colors text-sm md:text-base">
                  {category.name}
                </h3>
                <p className="text-xs text-muted-foreground line-clamp-2 hidden md:block">{category.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bestsellers */}
      {bestsellers.length > 0 && (
        <section className="py-12 bg-surface">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Flame className="w-5 h-5 text-brand-orange" />
                  <span className="text-xs font-bold uppercase tracking-wide text-brand-orange">
                    Top ventes
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'IBM Plex Sans' }}>
                  Les meilleures ventes
                </h2>
              </div>
              <Link to="/products?sort_by=popularity" className="text-brand-orange hover:underline text-sm font-medium hidden md:flex items-center gap-1">
                Tout voir <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {bestsellers.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Two columns: New Arrivals + On Sale */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* New Arrivals */}
          {newArrivals.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-brand-orange" />
                  <h2 className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'IBM Plex Sans' }}>
                    Nouveautés
                  </h2>
                </div>
                <Link to="/products?sort_by=newest" className="text-brand-orange hover:underline text-sm font-medium">
                  Voir tout
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {newArrivals.slice(0, 4).map((product) => (
                  <ProductCard key={product.id} product={product} compact />
                ))}
              </div>
            </div>
          )}
          {/* On Sale */}
          {onSale.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-destructive" />
                  <h2 className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'IBM Plex Sans' }}>
                    Promotions
                  </h2>
                </div>
                <Link to="/products" className="text-brand-orange hover:underline text-sm font-medium">
                  Voir tout
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {onSale.slice(0, 4).map((product) => (
                  <ProductCard key={product.id} product={product} compact />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Reassurance Section */}
      <section className="py-12 bg-brand-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Truck, title: 'Livraison rapide', desc: '24-48h sur toute la France métropolitaine' },
              { icon: Shield, title: 'Paiement sécurisé', desc: 'Stripe certifié PCI DSS, CB & PayPal' },
              { icon: HeadphonesIcon, title: 'Service client', desc: 'Notre équipe disponible 6j/7 par téléphone & email' },
              { icon: Award, title: 'Qualité garantie', desc: 'Garantie 2 ans sur tous nos produits' },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="w-14 h-14 bg-brand-orange rounded-sm flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>{item.title}</h3>
                  <p className="text-sm text-white/70">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
